'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { useSiteData } from '@/lib/dataCache';
import {
  Globe,
  Code,
  ShoppingCart,
  Palette,
  Layout,
  Lightbulb,
  ArrowRight,
  Server,
  Smartphone,
  Cloud,
  Database,
  Rocket,
  PenTool,
  Wrench,
  LineChart,
  type LucideIcon,
} from 'lucide-react';

const ICON_MAP: Record<string, LucideIcon> = {
  globe: Globe,
  code: Code,
  'shopping-cart': ShoppingCart,
  shoppingcart: ShoppingCart,
  cart: ShoppingCart,
  palette: Palette,
  layout: Layout,
  lightbulb: Lightbulb,
  server: Server,
  smartphone: Smartphone,
  mobile: Smartphone,
  cloud: Cloud,
  database: Database,
  rocket: Rocket,
  pen: PenTool,
  pentool: PenTool,
  design: PenTool,
  wrench: Wrench,
  tool: Wrench,
  chart: LineChart,
  analytics: LineChart,
};

const ACCENTS = [
  { from: '#6C63FF', to: '#8D84FF' },
  { from: '#FF6584', to: '#FF9AB0' },
  { from: '#22c55e', to: '#4ade80' },
  { from: '#f59e0b', to: '#fbbf24' },
  { from: '#06b6d4', to: '#22d3ee' },
  { from: '#a855f7', to: '#c084fc' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

type ApiService = {
  id: string;
  title: string;
  description: string;
  icon: string;
};

export default function Services() {
  const services = useSiteData<ApiService[]>('services', '/api/services', []);

  return (
    <section id="services" className="services-section">
      <div className="services-blob services-blob-1" />
      <div className="services-blob services-blob-2" />

      <div className="section-container">
        <motion.div
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <span className="section-label">What I Offer</span>
          <h2 className="section-title">Services I Provide</h2>
          <p className="section-subtitle">
            End-to-end digital solutions tailored to your goals — from design
            to deployment and everything in between.
          </p>
        </motion.div>

        <div className="services-grid">
          {services.length === 0 && (
            <div className="services-empty">No services published yet.</div>
          )}
          {services.map((service, i) => {
            const iconKey = (service.icon || 'globe').toLowerCase().trim();
            const Icon = ICON_MAP[iconKey] ?? Globe;
            const accent = ACCENTS[i % ACCENTS.length];
            return (
              <motion.div
                key={service.id}
                className="services-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: 'easeOut' }}
                style={
                  {
                    '--accent-from': accent.from,
                    '--accent-to': accent.to,
                  } as React.CSSProperties
                }
              >
                <div className="services-card-glow" />

                <div className="services-icon-box">
                  <Icon size={26} strokeWidth={2} />
                </div>

                <h3 className="services-card-title">{service.title}</h3>
                <p className="services-card-desc">{service.description}</p>

                <Link href="/services" className="services-card-link">
                  <span>Learn more</span>
                  <ArrowRight size={16} className="services-card-arrow" />
                </Link>

                <span className="services-card-number">
                  {String(i + 1).padStart(2, '0')}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
